import { isNil } from 'lodash';

import { isPlainObject, objectLabel } from './promotionsUtils';
import type { PromotionChange } from './types';

// The Metabase reports adapter's configuration type (mirrors the backend's `MetabaseReportsConfigRepoManager`).
const METABASE_REPORTS_CONFIG_TYPE = 'metabase_reports';

/** A selectable Metabase change → every pending CREATE change it needs, transitively (a dashboard on a card on
 * another card needs both cards). Only roots that need at least one CREATE are present. */
export type RequirementGraph = Map<string, Set<string>>;

/** The rows a selection pins on: `requiredIds` are the CREATE changes some selected item needs, and
 * `requiredBy` names, for each of them, the selected items that need it (sorted labels). */
export interface DependencyLocks {
  requiredIds: Set<string>;
  requiredBy: Map<string, string[]>;
}

/** A serialized `MetabaseReportItem` as it arrives in a change's `after_value`: its logical `key`
 * (`<model>:<encoded name path>`) and the logical keys it references, in `ref_map` (by entity id) and
 * `numeric_ref_map` (by numeric card id). A key with no counterpart in the export maps to null. */
interface MetabaseItemValue {
  key: string;
  model?: string;
  ref_map?: Record<string, unknown>;
  numeric_ref_map?: Record<string, unknown>;
}

/** Whether a change's `after_value` is a Metabase item this module can read: a string `key`, an optional
 * string `model`, and reference maps that are objects when present. Anything else is ignored, never read. */
function isMetabaseItemValue(value: unknown): value is MetabaseItemValue {
  return (
    isPlainObject(value) &&
    typeof value.key === 'string' &&
    (isNil(value.model) || typeof value.model === 'string') &&
    (isNil(value.ref_map) || isPlainObject(value.ref_map)) &&
    (isNil(value.numeric_ref_map) || isPlainObject(value.numeric_ref_map))
  );
}

/** The logical keys an item references, other than its own. */
function directRefs(item: MetabaseItemValue): string[] {
  return [...Object.values(item.ref_map ?? {}), ...Object.values(item.numeric_ref_map ?? {})].filter(
    (ref): ref is string => typeof ref === 'string' && ref !== item.key
  );
}

/** The encoded path above the last unescaped `/` of `encodedPath`, or null for a single segment. Mirrors the
 * backend's `_split_encoded_path`: a backslash escapes the character after it, so a `/` inside a name (stored
 * as `\/`) never separates two segments. */
function parentOfEncodedPath(encodedPath: string): string | null {
  let lastSeparator = -1;
  let index = 0;
  while (index < encodedPath.length) {
    if (encodedPath[index] === '\\') {
      index += 2;
    } else {
      if (encodedPath[index] === '/') {
        lastSeparator = index;
      }
      index += 1;
    }
  }
  return lastSeparator === -1 ? null : encodedPath.slice(0, lastSeparator);
}

/** The keys of the questions saved inside a dashboard item. A question is never an item of its own: it arrives
 * with its dashboard, under a key naming it as though the dashboard were its collection
 * (`card:<dashboard path>/<question>`, the backend's `_member_key`), and the dashboard's ref_map lists it. A card
 * in a same-named collection beside the dashboard has a key of the same shape, so only a key the dashboard
 * itself lists counts. */
function dashboardQuestionKeys(item: MetabaseItemValue): string[] {
  if (!item.key.startsWith('dashboard:')) {
    return [];
  }
  const dashboardPath = item.key.slice('dashboard:'.length);
  return directRefs(item).filter(
    (ref) => ref.startsWith('card:') && parentOfEncodedPath(ref.slice('card:'.length)) === dashboardPath
  );
}

/** Every pending CREATE `rootId` needs, following each needed change's own references breadth-first. The visited
 * set ends a reference cycle, and a root never needs itself. */
function requiredCreates(
  rootId: string,
  refsById: Map<string, string[]>,
  createIdByRef: Map<string, string>
): Set<string> {
  const required = new Set<string>();
  const queue = [rootId];
  for (let index = 0; index < queue.length; index += 1) {
    for (const ref of refsById.get(queue[index]) ?? []) {
      const changeId = createIdByRef.get(ref);
      if (!isNil(changeId) && changeId !== rootId && !required.has(changeId)) {
        required.add(changeId);
        queue.push(changeId);
      }
    }
  }
  return required;
}

/** The dependency graph of a preview's Metabase changes, read from the edges every item already carries.
 *
 * Only a sibling CREATE is ever required: a referenced item with no pending CREATE is already on the target
 * (or has only a MODIFY, which applies against the target's existing copy), and a collection is never
 * required because the apply stubs a missing one. Every non-DELETE Metabase change is a root, so a card
 * built on another card locks it exactly as a dashboard locks its cards. */
export function computeMetabaseRequirements(changes: PromotionChange[]): RequirementGraph {
  const refsById = new Map<string, string[]>();
  const createIdByKey = new Map<string, string>();
  const createIdByQuestionKey = new Map<string, string>();
  const rootIds: string[] = [];
  for (const change of changes) {
    const item = change.after_value;
    if (change.configuration_type !== METABASE_REPORTS_CONFIG_TYPE || !isMetabaseItemValue(item)) {
      continue;
    }
    refsById.set(change.change_id, directRefs(item));
    if (change.change_type === 'CREATE' && item.model !== 'collection') {
      createIdByKey.set(item.key, change.change_id);
      // A question saved inside a new dashboard arrives with it, so a reference to it needs the dashboard.
      dashboardQuestionKeys(item).forEach((key) => createIdByQuestionKey.set(key, change.change_id));
    }
    if (change.change_type !== 'DELETE') {
      rootIds.push(change.change_id);
    }
  }
  // A key that a pending CREATE carries is that item, never a dashboard's question: a card the dashboard shows from
  // a same-named collection beside it has a key of the same shape. When that card is already on the target, its key
  // stays mapped to the dashboard, so another new card that references it locks the dashboard too. That only
  // over-selects: the apply still resolves the target's own card.
  const createIdByRef = new Map([...createIdByQuestionKey, ...createIdByKey]);

  const graph: RequirementGraph = new Map();
  rootIds.forEach((rootId) => {
    const required = requiredCreates(rootId, refsById, createIdByRef);
    if (required.size > 0) {
      graph.set(rootId, required);
    }
  });
  return graph;
}

/** The rows `explicitSelection` pins on. Each root's set in `graph` is already transitive, so one pass over
 * the selected roots finds every requirement. `requiredBy` names only the items the user selected, so the
 * label says which of their choices holds the row. A required row the user also selected is still locked
 * while it is required. */
export function computeDependencyLocks(
  graph: RequirementGraph,
  explicitSelection: Set<string>,
  changes: PromotionChange[]
): DependencyLocks {
  const requiredBy = new Map<string, string[]>();
  changes
    .filter((root) => explicitSelection.has(root.change_id))
    .forEach((root) => {
      graph.get(root.change_id)?.forEach((requiredId) => {
        requiredBy.set(requiredId, [...(requiredBy.get(requiredId) ?? []), objectLabel(root)]);
      });
    });
  requiredBy.forEach((labels) => labels.sort());
  return { requiredIds: new Set(requiredBy.keys()), requiredBy };
}
